'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { getCookie } from 'cookies-next';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { cookiesIdHandler } from 'middleware/cookieMiddlewareIdHandler';
import getFeatures from 'services/growthbook/getFeatures';
import { handleExperimentEvent } from './experiments';

export type growthBookParams = {
  sessionId: string;
  loggedIn: boolean;
  browser: string;
  url: string;
  eventId: string;
};

const growthbookInstance = new GrowthBook({
  enableDevMode: !!getCookie('growthbook_dev_mode'),
  trackingCallback(experiment, result) {
    handleExperimentEvent({ experiment, result });
  },
});

async function fetchFeature() {
  try {
    const { features } = await getFeatures();
    growthbookInstance?.setFeatures(features);
    return features;
  } catch (error) {
    throw new Error('Error fetching GrowthBook features');
  }
}

function setAttributes(params: growthBookParams) {
  growthbookInstance.setAttributes(params);
}

type GrowthbookContextProps = {
  children: React.ReactNode;
};

function GrowthbookContext({ children }: GrowthbookContextProps) {
  const pathname = usePathname();

  useEffect(() => {
    async function initGrowthBook() {
      let sessionId = cookiesIdHandler().getSessionId();
      if (!sessionId) sessionId = cookiesIdHandler().createSessionId();

      await fetchFeature();
      setAttributes({
        sessionId: sessionId as string,
        loggedIn: false,
        browser: navigator.userAgent,
        url: window.location.href,
        eventId: '0',
      });
    }

    initGrowthBook();
  }, [pathname]);

  return <GrowthBookProvider growthbook={growthbookInstance}>{children}</GrowthBookProvider>;
}

export default GrowthbookContext;
